/**
 * Startseite.
 *
 * Beantwortet die drei Fragen, mit denen Bernd morgens ins Auto steigt:
 * Was ist fällig? Wo läuft noch eine Prüfung? Wo sind offene Mängel?
 */

import { Link } from 'react-router-dom'
import { useKunden, usePruefungen, useSpielplaetze } from '../api/abfragen'
import { useAufgaben } from '../api/aufgaben'
import { Kontrollmeldungen } from '../bausteine/Kontrollmeldungen'
import { PRUEFARTTEXT } from '../api/typen'
import { Kennzahl, Ladeanzeige, Leerhinweis, datumDeutsch } from '../bausteine/Hinweise'
import { ErsteSchritte } from '../bausteine/ErsteSchritte'
import { useMandant } from '../api/verwaltung'
import { darfVerwalten, useKonto } from '../konto/KontoKontext'

export function Uebersicht() {
  const { konto } = useKonto()
  const kunden = useKunden()
  const plaetze = useSpielplaetze()
  const laufende = usePruefungen('laufend')
  const entwuerfe = usePruefungen('entwurf')
  const alle = usePruefungen()
  // Nur die Verwaltung darf die Unternehmensdaten lesen; für alle anderen
  // bleibt der Schritt in der Einleitung ohnehin ausgeblendet.
  const mandant = useMandant()
  /* Fällig kommt aus den Prüfaufgaben, nicht aus `naechste_pruefung_am`
     des Platzes.

     Der alte Weg las das nächste Prüfdatum der letzten abgeschlossenen
     Prüfung. Ein frisch angelegter Platz hat keine — er stand deshalb nie
     unter „Fällig“, obwohl seine Aufgabe für heute eingeplant war. Zwei
     verschiedene Vorstellungen davon, was fällig ist, und die Übersicht
     hatte die falsche. */
  const aufgaben = useAufgaben(true)

  /* Alle Hooks stehen vor dem ersten `return`.

     Genau hier stand `useAufgaben` vorher darunter, und das hat die App
     zur weißen Seite gemacht: solange geladen wurde, stieg die Funktion
     vorher aus und rief einen Hook weniger auf. Kamen die Daten an, lief
     sie durch — ein Hook mehr als beim vorigen Aufbau, und React bricht
     den ganzen Baum ab (Fehler #310). Beim zweiten Öffnen fiel es nicht
     auf, weil aus dem Zwischenspeicher kein Ladebild mehr kommt. */
  if (kunden.isLoading || plaetze.isLoading) return <Ladeanzeige />

  const offeneMaengel = (plaetze.data ?? []).reduce((summe, p) => summe + p.offene_maengel, 0)
  const heute = new Date().toISOString().slice(0, 10)

  const faellig = (aufgaben.data ?? [])
    .filter((a) => a.faellig_am <= heute)
    .sort((a, b) => a.faellig_am.localeCompare(b.faellig_am))
  const offeneBoegen = [...(laufende.data ?? []), ...(entwuerfe.data ?? [])]

  return (
    <div className="space-y-6">
      <ErsteSchritte
        kunden={(kunden.data ?? []).length}
        plaetze={(plaetze.data ?? []).length}
        pruefungen={(alle.data ?? []).length}
        unternehmenVollstaendig={mandant.data?.rechnungsfaehig}
      />

      <header>
        <h1 className="text-2xl font-bold text-slate-900">Guten Tag, {konto?.name}</h1>
        <p className="text-slate-700">{konto?.mandant_name}</p>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kennzahl wert={kunden.data?.length ?? 0} beschriftung="Kunden" />
        <Kennzahl wert={plaetze.data?.length ?? 0} beschriftung="Spielplätze" />
        <Kennzahl wert={offeneBoegen.length} beschriftung="offene Prüfungen" />
        <Kennzahl
          wert={offeneMaengel}
          beschriftung="offene Mängel"
          betont={offeneMaengel > 0}
        />
      </div>

      {offeneBoegen.length > 0 && (
        <section>
          <h2 className="ueberschrift-akzent mb-3 text-xl">Weiterarbeiten</h2>
          <ul className="space-y-2">
            {offeneBoegen.map((p) => (
              <li key={p.id}>
                <Link to={`/pruefungen/${p.id}/bogen`} className="karte-knopf">
                  <div className="flex-1">
                    <div className="font-semibold">{p.spielplatz_name}</div>
                    <div className="text-base text-slate-700">
                      {p.kunde_name} · {PRUEFARTTEXT[p.pruefart]} · {datumDeutsch(p.datum)}
                    </div>
                  </div>
                  <span className="marke bg-marke-100 text-marke-700">
                    {p.status === 'entwurf' ? 'Entwurf' : 'läuft'}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Vor „Fällig": eine Beanstandung vom Bauhof ist der häufigere Anlass,
          kurzfristig auf einen Platz zu fahren, als ein Termin aus der
          Fälligkeit. Ist nichts gemeldet, erscheint der Abschnitt nicht. */}
      <Kontrollmeldungen />

      <section>
        <h2 className="ueberschrift-akzent mb-3 text-xl">
          Fällig {faellig.length > 0 && <span className="text-stufe-2">({faellig.length})</span>}
        </h2>
        {faellig.length === 0 ? (
          <Leerhinweis
            titel="Heute nichts fällig"
            text="Termine planen Sie auf der Seite eines Spielplatzes ein."
          />
        ) : (
          <ul className="space-y-2">
            {faellig.map((a) => {
              const ueberfaellig = a.faellig_am < heute
              return (
                <li key={a.id}>
                  <Link to={`/spielplaetze/${a.spielplatz_id}`} className="karte-knopf">
                    <div className="flex-1">
                      <div className="font-semibold">{a.spielplatz_name}</div>
                      <div className="text-base text-slate-700">
                        {a.kunde_name} · {PRUEFARTTEXT[a.pruefart]}
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className={`font-semibold ${
                          ueberfaellig ? 'text-stufe-3' : 'text-stufe-2'
                        }`}
                      >
                        {datumDeutsch(a.faellig_am)}
                      </div>
                      <div className="text-sm text-slate-600">
                        {ueberfaellig ? 'überfällig' : 'heute'}
                      </div>
                    </div>
                  </Link>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      {offeneMaengel > 0 && (
        <section>
          <h2 className="ueberschrift-akzent mb-3 text-xl">Plätze mit offenen Mängeln</h2>
          <ul className="space-y-2">
            {(plaetze.data ?? [])
              .filter((p) => p.offene_maengel > 0)
              .map((p) => (
                <li key={p.id}>
                  <Link to={`/spielplaetze/${p.id}`} className="karte-knopf">
                    <div className="flex-1">
                      <div className="font-semibold">{p.name}</div>
                      <div className="text-base text-slate-700">{p.kunde_name}</div>
                    </div>
                    <span className="marke bg-stufe-3-grund text-stufe-3">
                      {p.offene_maengel} offen
                    </span>
                  </Link>
                </li>
              ))}
          </ul>
        </section>
      )}

      {/* Die Kopfzeile blendet diese Wege auf schmalen Bildschirmen aus – der
          Platz reicht dort nicht. Damit sie auf dem Handy trotzdem erreichbar
          sind, stehen sie hier am Ende der Startseite. */}
      <section className="sm:hidden">
        <h2 className="ueberschrift-akzent mb-3 text-xl">Weiteres</h2>
        <div className="grid grid-cols-2 gap-2">
          <Link to="/bausteine" className="karte-knopf justify-center font-semibold">
            Kataloge
          </Link>
          <Link to="/pruefablaeufe" className="karte-knopf justify-center font-semibold">
            Prüfabläufe
          </Link>
          <Link to="/konto" className="karte-knopf justify-center font-semibold">
            Mein Konto
          </Link>
          {darfVerwalten(konto) && (
            <Link to="/verwaltung" className="karte-knopf justify-center font-semibold">
              Verwaltung
            </Link>
          )}
          <Link to="/warteschlange" className="karte-knopf justify-center font-semibold">
            Warteschlange
          </Link>
        </div>
      </section>
    </div>
  )
}
